/**
 * 天氣彈窗模組 - WeatherModal
 * 
 * 版本：V1.02 | 更新日期：2025-07-16 16:00:00
 * 
 * 檔案功能：提供天氣預報查詢和顯示功能，解析 API 回傳的 morning/afternoon/summary 結構
 * 解決問題：讓用戶能查看特定日期和市場的天氣預報資訊，顯示內容與舊版一致
 * 
 * 更新內容：
 * - V1.02: 修正資料結構解析，與 Cloud Function 回傳格式一致，顯示 summary、morning、afternoon
 * - V1.01: 初始版本，從 App.js 模組化搬移
 * 
 * 注意事項：
 * - 依賴 Firebase Functions 的 getWeatherForecast 函數
 * - 關連檔案：App.js, functions/index.js
 */

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';

const WeatherModal = ({ config, onClose, functions }) => {
    const { isOpen, city, date, marketName } = config;
    const [weather, setWeather] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // 【AI承諾】當彈窗開啟時自動載入天氣資料
    useEffect(() => {
        if (isOpen && city && date) {
            loadWeatherData();
        }
        // eslint-disable-next-line
    }, [isOpen, city, date]);

    /**
     * 載入天氣資料
     * Why: 透過 Firebase Functions 呼叫外部天氣 API，獲取準確的天氣預報
     */
    const loadWeatherData = async () => {
        setIsLoading(true);
        setError('');
        setWeather(null);
        try {
            const getWeatherForecast = httpsCallable(functions, 'getWeatherForecast');
            const result = await getWeatherForecast({ city, date });
            if (result.data && result.data.success && result.data.weather) {
                setWeather(result.data.weather);
            } else {
                setError(result.data?.error || '天氣查詢失敗，請稍後再試');
            }
        } catch (err) {
            setError('天氣查詢失敗，請稍後再試');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    // 解析天氣摘要
    const summary = weather?.summary;
    const morning = weather?.morning;
    const afternoon = weather?.afternoon;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">天氣預報</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                        ×
                    </button>
                </div>

                <div className="mb-4">
                    <p className="text-gray-600">
                        <span className="font-semibold">市場：</span>{marketName}
                    </p>
                    <p className="text-gray-600">
                        <span className="font-semibold">日期：</span>{date}
                    </p>
                    <p className="text-gray-600">
                        <span className="font-semibold">地區：</span>{city}
                    </p>
                </div>

                {isLoading && (
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="mt-2 text-gray-600">查詢天氣中...</p>
                    </div>
                )}

                {error && (
                    <div className="text-center py-4">
                        <p className="text-red-500">{error}</p>
                        <button 
                            onClick={loadWeatherData}
                            className="mt-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                        >
                            重新查詢
                        </button>
                    </div>
                )}

                {/* 顯示天氣摘要與分時段資料 */}
                {weather && !isLoading && (
                    <div className="space-y-4">
                        {/* 天氣摘要 */}
                        {summary && (
                            <div className={`p-4 rounded-lg text-sm ${summary.isBadWeather ? 'bg-orange-100 border border-orange-300 text-orange-800' : 'bg-green-100 border border-green-300 text-green-800'}`}>
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">{summary.isBadWeather ? '⚠️' : '✅'}</span>
                                    <span className="font-medium">
                                        {summary.isBadWeather ? '建議評估天氣風險' : '天氣適合擺攤'}
                                    </span>
                                </div>
                                <p className="mt-2 text-xs">{summary.description}</p>
                                {summary.recommendation && <p className="mt-1 text-xs">{summary.recommendation}</p>}
                            </div>
                        )}

                        {/* 上午、下午分時段天氣 */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {morning && (
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <div className="font-medium text-blue-700 mb-2 flex items-center gap-2">
                                        <span>🌅</span>
                                        <span>上午 (06-12時)</span>
                                    </div>
                                    <div className="space-y-1 text-sm">
                                        <div><span className="font-medium">天氣：</span>{morning.weather || '無資料'}</div>
                                        <div><span className="font-medium">溫度：</span>{morning.minTemp ?? '-'}° ~ {morning.maxTemp ?? '-'}°C</div>
                                        <div><span className="font-medium">降雨：</span>{morning.rainProbability ?? '-'}%</div>
                                        {morning.comfort && <div><span className="font-medium">舒適：</span>{morning.comfort}</div>}
                                    </div>
                                </div>
                            )}
                            {afternoon && (
                                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                                    <div className="font-medium text-orange-700 mb-2 flex items-center gap-2">
                                        <span>☀️</span>
                                        <span>下午 (12-18時)</span>
                                    </div>
                                    <div className="space-y-1 text-sm">
                                        <div><span className="font-medium">天氣：</span>{afternoon.weather || '無資料'}</div>
                                        <div><span className="font-medium">溫度：</span>{afternoon.minTemp ?? '-'}° ~ {afternoon.maxTemp ?? '-'}°C</div>
                                        <div><span className="font-medium">降雨：</span>{afternoon.rainProbability ?? '-'}%</div>
                                        {afternoon.comfort && <div><span className="font-medium">舒適：</span>{afternoon.comfort}</div>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="mt-6 flex justify-end">
                    <button 
                        onClick={onClose}
                        className="bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-300"
                    >
                        關閉
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WeatherModal; 